import { z } from 'zod';
import { DEFAULT_BILLING_RATE } from './organization-defaults';
import { CapacitySourceSchema } from './capacity-priority';
/**
 * Constants for working days calculation
 * Based on 220 working days per year (52 weeks * 5 days - holidays/vacation)
 */ export const WORKING_DAYS_PER_YEAR = 220;
/**
 * Support staff multiplier (30% of base capacity per support person)
 */ export const SUPPORT_STAFF_MULTIPLIER = 0.3;
/**
 * Frequency values and their yearly multipliers
 * Based on 220 working days per year
 */ export const FREQUENCY_VALUES = {
    'multiple-times-daily': 660,
    'once-a-day': 220,
    'several-times-a-week': 156,
    'once-per-week': 52,
    'every-two-weeks': 26,
    'every-three-weeks': 17.3,
    monthly: 12,
    quarterly: 4,
    'semi-annually': 2,
    yearly: 1
};
/**
 * Frequency value schema (Zod enum)
 */ export const FrequencyValueSchema = z.enum([
    'multiple-times-daily',
    'once-a-day',
    'several-times-a-week',
    'once-per-week',
    'every-two-weeks',
    'every-three-weeks',
    'monthly',
    'quarterly',
    'semi-annually',
    'yearly'
]);
/**
 * The "mehrmals" frequency options, mapped to the period base their occurrence
 * count is multiplied by to reach a per-year figure. `timesPerPeriod × base`.
 */ export const MULTI_FREQUENCY_PERIOD_BASE = {
    'multiple-times-daily': WORKING_DAYS_PER_YEAR,
    'several-times-a-week': 52
};
/**
 * Default occurrence count prefilled in the UI for each "mehrmals" option.
 * Matches the legacy hardcoded defaults (3×220=660, 3×52=156) so a survey
 * recorded with the default produces exactly the pre-feature number.
 */ export const MULTI_FREQUENCY_DEFAULT_COUNT = {
    'multiple-times-daily': 3,
    'several-times-a-week': 3
};
/**
 * Whether a frequency value carries an explicit per-period occurrence count.
 */ export function isMultiFrequency(value) {
    return value in MULTI_FREQUENCY_PERIOD_BASE;
}
/**
 * Fixed per-source confidence, in code rather than per tenant. Only sources
 * whose confidence is a constant appear here: survey confidence is the computed
 * response coverage, so `survey` has no entry.
 */ export const SOURCE_CONFIDENCE = {
    manual: 0.9
};
/**
 * Capacity binding schema
 * Represents calculated time/cost binding for a process, system, user, etc.
 */ export const CapacityBindingSchema = z.object({
    hoursPerMonth: z.number(),
    hoursPerYear: z.number(),
    costPerMonth: z.number(),
    costPerYear: z.number(),
    billingRate: z.number(),
    stepsWithData: z.number(),
    totalSteps: z.number(),
    /**
   * Which source produced this binding's value — set only on a PER-STEP
   * binding, where exactly one source won the chain walk. Absent on every
   * aggregate, whose steps may have won from different sources.
   */ capacitySource: CapacitySourceSchema.optional(),
    /**
   * At least one summed step is effective from a manually entered value. The
   * provenance trace every surface annotates with, including the list surfaces
   * that receive nothing but this process-level binding.
   */ includesManualValues: z.boolean().optional(),
    /**
   * Response coverage as a 0..1 share (ADR 0023 decision 4): how many of the
   * people expected to answer for these steps did — completed or dismissed.
   * Derived at read time, never stored.
   *
   * PROCESS DETAIL ONLY — that is the one read path that resolves an audience.
   * It is `null` wherever no step carried one: every system/entity aggregate
   * (which is also every binding {@link calculateCapacityMap} serves, so the
   * process LIST reads null for a process whose detail page reads 80 %), and
   * every personal binding — the personal pipeline reads one user's own row and
   * has no audience to measure against, so a faked 1.0 would be the one number
   * a reader must never see here.
   *
   * It is ALSO `null` when the capacity weight sum is zero — every step's
   * `adjustedCapacityPerYear` is 0, as an unrecognised frequency produces — so
   * this field can read null on a process whose per-step coverage map carries
   * real numbers. A null here is never evidence that no step was covered.
   */ confidence: z.number().min(0).max(1).nullable().optional()
});
/**
 * Both sources' bindings side by side, for the surfaces that show the shadowed
 * value next to the effective one (detail popover, editor capacity section).
 * Each side is the counterfactual "what would this be under that source alone",
 * so both can carry a value at once; `null` where that source has none.
 */ export const CapacitySourcesSchema = z.object({
    survey: CapacityBindingSchema.nullable(),
    manual: CapacityBindingSchema.nullable()
});
/**
 * One process-scoped capacity computation, exactly what the calculator's
 * `calculateStepCapacities` returns.
 */ export const ProcessCapacitySchema = z.object({
    overall: CapacityBindingSchema.nullable(),
    perStep: z.record(z.string(), CapacityBindingSchema),
    /**
   * Per-step response coverage as a 0..1 share (ADR 0023 decision 4),
   * derived from the same survey answers as `perStep` and sensitive for
   * the same reason.
   *
   * A WIDER map than `perStep`: a step whose rows are all declines or
   * still open has no capacity binding at all, and is exactly the step
   * whose coverage is worth reading. A step nobody can be asked about
   * appears in neither.
   */ coverage: z.record(z.string(), z.number().min(0).max(1)),
    bySource: z.object({
        /**
     * Both sources' counterfactual totals behind `overall`, for the detail
     * page's Kapazitätsquellen popover (PRD 8.3) — the same survey-derived
     * numbers split by provenance, plus the manual totals the same viewer
     * may not read.
     */ overall: CapacitySourcesSchema,
        /** The same split per step, keyed like `perStep`. */ perStep: z.record(z.string(), CapacitySourcesSchema)
    })
});
// ============================================================================
// Pure Calculation Functions
// ============================================================================
/**
 * Get the yearly and monthly frequency multipliers for a frequency value.
 *
 * For "mehrmals" options, an explicit `timesPerPeriod` (e.g. 5×/day) yields
 * `timesPerPeriod × periodBase`. When omitted — as on every legacy survey — we
 * fall back to FREQUENCY_VALUES, which encodes the historical 3×/period default.
 */ export function getFrequencyMultipliers(frequency, timesPerPeriod) {
    const base = MULTI_FREQUENCY_PERIOD_BASE[frequency];
    const perYear = base != null && timesPerPeriod != null && timesPerPeriod > 0 ? timesPerPeriod * base : FREQUENCY_VALUES[frequency] ?? 0;
    const perMonth = perYear / 12;
    return {
        perYear,
        perMonth
    };
}
/**
 * Calculate base capacity for a single step
 * @param frequencyPerYear - How many times per year the step is performed
 * @param durationMinutes - How long the step takes in minutes
 * @returns Capacity in hours per year
 */ export function calculateStepCapacity(frequencyPerYear, durationMinutes) {
    return frequencyPerYear * (durationMinutes / 60);
}
/**
 * Calculate team capacity with extrapolation for partial survey responses
 * Full specification: sum individual surveys + extrapolate remainder
 *
 * @param baseCapacity - The original recorder's capacity (hours per year)
 * @param totalTeamMembers - Total number of team members
 * @param surveyCount - Number of completed surveys
 * @param surveyCapacities - Array of capacity values from completed surveys
 * @returns Total team capacity in hours per year
 */ export function calculateTeamCapacity(baseCapacity, totalTeamMembers, surveyCount, surveyCapacities) {
    if (totalTeamMembers <= 0) {
        // No derived members (e.g. everyone left the OrgFunction) but surveys
        // remain: sum what we have rather than dropping all but the first.
        return surveyCapacities.reduce((sum, cap)=>sum + cap, 0);
    }
    if (surveyCount >= totalTeamMembers) {
        // All members have surveys - sum their capacities
        return surveyCapacities.reduce((sum, cap)=>sum + cap, 0);
    }
    // Partial surveys - sum filled surveys + extrapolate remainder
    const filledTotal = surveyCapacities.reduce((sum, cap)=>sum + cap, 0);
    const remainingMembers = totalTeamMembers - surveyCount;
    const extrapolated = baseCapacity * remainingMembers;
    return filledTotal + extrapolated;
}
/**
 * Calculate capacity with support staff factor
 * Each support person adds 30% of the base capacity
 *
 * @param baseCapacity - Base capacity in hours per year
 * @param supportCount - Number of support persons (helpers.persons)
 * @returns Adjusted capacity in hours per year
 */ export function calculateWithSupportStaff(baseCapacity, supportCount) {
    if (supportCount <= 0) {
        return baseCapacity;
    }
    return baseCapacity + baseCapacity * supportCount * SUPPORT_STAFF_MULTIPLIER;
}
/**
 * Aggregate one step's per-survey capacities into its adjusted yearly capacity
 * (ADR 0009), branching on the assignee, then adding the support-staff factor:
 *
 * - OrgFunction kind `role` (Funktion): mean of responders — any holder can do
 *   the step but only one does it at a time, so it's a single stream.
 * - OrgFunction kind `team` (Team): sum responders + extrapolate non-responders
 *   ({@link calculateTeamCapacity}) — all members do it together. Members who
 *   dismissed their survey ("Ich mache diesen Schritt nicht", ADR 0022) leave
 *   the denominator: they are still OrgFunction members, so `teamMemberCount`
 *   counts them, and extrapolation would otherwise add back at base capacity
 *   exactly the people who said they contribute none.
 * - User (and anything else with surveys, e.g. whoElse on a non-human assignee):
 *   sum — each performer does it independently.
 *
 * The single home for the kind-aware decision: every caller reaches it through
 * `calculateStepCapacityData` (`capacity-binding.ts`) — the request-scoped
 * CapacityCalculatorService and the app-runner's DataProxyHost, which is how
 * the analyzer gets these numbers. There is no second implementation.
 */ export function aggregateStepCapacity(params) {
    const { assignedToModel, orgFunctionKind, teamMemberCount, dismissedCount, surveyCount, surveyCapacities, baseCapacity, helperPersonCount } = params;
    const total = surveyCapacities.reduce((sum, cap)=>sum + cap, 0);
    let adjusted;
    if (assignedToModel === 'OrgFunction' && orgFunctionKind === 'role') {
        adjusted = surveyCapacities.length > 0 ? total / surveyCapacities.length : 0;
    } else if (assignedToModel === 'OrgFunction' && teamMemberCount != null) {
        adjusted = calculateTeamCapacity(baseCapacity, teamMemberCount - dismissedCount, surveyCount, surveyCapacities);
    } else {
        adjusted = total;
    }
    // Support staff (helpers.persons) each add 30% of the per-performer base.
    if (helperPersonCount > 0) {
        adjusted += baseCapacity * helperPersonCount * SUPPORT_STAFF_MULTIPLIER;
    }
    return adjusted;
}
/**
 * One step's RESPONSE COVERAGE (ADR 0023 decision 4): the share of the people
 * expected to answer for this step who did — `completed` and `dismissed` alike,
 * because a decline is an answer. NOT "observed versus extrapolated": only
 * team-kind steps extrapolate at all ({@link calculateTeamCapacity}), while a
 * role averages its responders and a User-kind step sums them.
 *
 * The expected audience is a **deduplicated union of IDENTITIES**, never a sum
 * of counts:
 *
 * - `audience` — who the step's assignment says performs it today: every member
 *   of an assigned OrgFunction (team members and role holders alike), or the
 *   assigned User.
 * - `nominees` — the who-else **User** nominees who answer for themselves:
 *   `pending` and `accepted`, plus a `declined` one still holding a live
 *   dismissed row (the retained-decliner rule, ADR 0023 decision 1) — that row
 *   IS their answer.
 * - `rowHolders` — everyone actually holding a live row, so a performer who has
 *   since left the OrgFunction keeps counting until reconciliation sweeps them.
 *
 * Deduplication by identity is the whole point of the union: a nominee who is
 * also a member of the assigned team appears in two of the three sets, and a
 * `teamMemberCount + rowCount` sum would put them in the denominator twice and
 * cap the step below 100 % forever. Drift in the other direction is covered by
 * the same union — a joiner with no row yet is in `audience` alone and lowers
 * coverage, which is exactly what an unasked member should do.
 *
 * Callers pass identities the survey-applicability seam has ALREADY filtered,
 * and the filter is answer-BLIND (`isNeverApplicableSurvey`): a recipient the
 * row never asked anything belongs to neither side, while one who has since
 * answered stays in the denominator they answered for. An empty expected set is
 * `null`, never 0 — a step nobody can be asked about has no coverage to report,
 * and 0 would read as "nobody answered".
 */ export function calculateResponseCoverage(params) {
    const expected = new Set([
        ...params.audience,
        ...params.nominees,
        ...params.rowHolders
    ]);
    if (expected.size === 0) {
        return null;
    }
    const responded = new Set(params.responded.filter((userId)=>expected.has(userId)));
    return responded.size / expected.size;
}
/**
 * The capacity-weighted mean of the per-step coverage, which is what a whole
 * process reports: `Σ(adjustedCapacityPerYear × confidence) / Σ(adjustedCapacityPerYear)`.
 * Weighting by capacity rather than by step count is what keeps one
 * five-minutes-a-year step from moving the number a 2000-hour step earned.
 *
 * A step needs BOTH sides to contribute: a step with no coverage of its own is
 * skipped, and so is one whose capacity is not a finite weight — a
 * confidence-bearing step with no calculable capacity carries no weight and is
 * therefore excluded from the mean rather than counted as if it weighed 1.
 * Computed over the unrounded capacities, before {@link sumCapacities} rounds
 * anything. A zero or entirely-null weight sum has no mean: `null`.
 */ export function calculateCapacityWeightedConfidence(steps) {
    let weightedSum = 0;
    let weightSum = 0;
    for (const step of steps){
        if (step.confidence == null) continue;
        if (!Number.isFinite(step.adjustedCapacityPerYear)) continue;
        weightedSum += step.adjustedCapacityPerYear * step.confidence;
        weightSum += step.adjustedCapacityPerYear;
    }
    return weightSum > 0 ? weightedSum / weightSum : null;
}
/**
 * Calculate process cost from capacity hours
 *
 * @param capacityHours - Capacity in hours
 * @param billingRate - Billing rate in EUR per hour (default: 110)
 * @returns Cost in EUR
 */ export function calculateProcessCost(capacityHours, billingRate = DEFAULT_BILLING_RATE) {
    return capacityHours * billingRate;
}
/**
 * Sum multiple capacities into a single capacity binding
 *
 * @param capacities - Array of step capacity data
 * @param totalSteps - Total number of steps considered
 * @param billingRate - Billing rate in EUR per hour
 * @returns Combined CapacityBinding or null if no data
 */ export function sumCapacities(capacities, totalSteps, billingRate = DEFAULT_BILLING_RATE) {
    const validCapacities = capacities.filter((s)=>Number.isFinite(s.adjustedCapacityPerYear));
    if (validCapacities.length === 0) {
        return null;
    }
    const totalPerYear = validCapacities.reduce((sum, step)=>sum + step.adjustedCapacityPerYear, 0);
    const hoursPerMonth = totalPerYear / 12;
    const hoursPerYear = totalPerYear;
    const costPerMonth = calculateProcessCost(hoursPerMonth, billingRate);
    const costPerYear = calculateProcessCost(hoursPerYear, billingRate);
    return {
        hoursPerMonth: Math.round(hoursPerMonth * 100) / 100,
        hoursPerYear: Math.round(hoursPerYear * 100) / 100,
        costPerMonth: Math.round(costPerMonth * 100) / 100,
        costPerYear: Math.round(costPerYear * 100) / 100,
        billingRate,
        stepsWithData: validCapacities.length,
        totalSteps,
        confidence: calculateCapacityWeightedConfidence(validCapacities),
        // `capacitySource` is never inferred from a one-step sum — an aggregate can
        // legitimately hold exactly one data-bearing step. The per-step callers
        // attach it explicitly.
        ...validCapacities.some((step)=>step.capacitySource === 'manual') ? {
            includesManualValues: true
        } : {}
    };
}

//# sourceMappingURL=capacity.js.map